/**
 * DocRenderer — fetches a Markdown doc from `frontend/public/docs/${docPath}`
 * and renders it safely via {@link renderMarkdownSafely} from Wave 1.
 *
 * Behaviour contract (spec Req 4):
 *
 *  - On mount and whenever `docPath` changes, fetch the file at
 *    `${PUBLIC_URL || ""}/docs/${docPath}` with an `AbortController` so an
 *    in-flight request is cancelled on unmount / docPath change.
 *  - Cache the last 10 fetched docs in a module-level `Map` (FIFO eviction);
 *    a cache hit skips the network entirely.
 *  - Render one of three states: loading (subtle spinner), success (safe
 *    Markdown render), or error (the "This document is missing — the
 *    manifest may be stale. Please rebuild." fallback with `role="alert"`
 *    per Req 4.6).
 *  - After a successful render, if `anchor` is set, scroll the matching
 *    heading into view **within the render pane only** using
 *    `useLayoutEffect` — never `window.scrollTo` or `document.body`
 *    scrolling (Req 4.5).
 *  - Never uses `dangerouslySetInnerHTML` — the safe pipeline is enforced by
 *    ESLint rule `react/no-danger` at error level (Req 4.3).
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-4-docs-renderer-safety
 */
import * as React from "react";
import { renderMarkdownSafely } from "./renderMarkdownSafely";

// ---------------------------------------------------------------------------
// Module-level LRU cache (FIFO eviction, max 10 entries)
// ---------------------------------------------------------------------------
//
// A `Map` preserves insertion order, so `keys().next().value` is the oldest
// entry. On overflow we drop the oldest — FIFO eviction as the task spec
// specifies. Hits do NOT re-order (we don't touch the map on read), which is
// simpler than a true LRU and equally correct given the 10-entry ceiling.

const DOC_CACHE_MAX_SIZE = 10;
const docCache = new Map<string, string>();

function cachePut(key: string, value: string): void {
  if (docCache.has(key)) {
    // Already cached — leave insertion order alone to keep eviction cheap.
    return;
  }
  docCache.set(key, value);
  if (docCache.size > DOC_CACHE_MAX_SIZE) {
    const firstKey = docCache.keys().next().value;
    if (firstKey !== undefined) {
      docCache.delete(firstKey);
    }
  }
}

/**
 * Test-only helper — clears the module-level cache so unit tests start
 * from a known state. NOT part of the public API.
 *
 * @internal
 */
export function __clearDocCacheForTests(): void {
  docCache.clear();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Props accepted by {@link DocRenderer}.
 */
export interface DocRendererProps {
  /**
   * Manifest-relative doc path, e.g. `"api/statements.md"`.
   */
  docPath: string;

  /**
   * Optional heading slug to scroll to within the render pane after render,
   * e.g. `"collection-rate"`. MUST match `/^[a-z0-9-]+$/` (validated by
   * upstream `parseDeepLink`).
   */
  anchor?: string;

  /**
   * Invoked when the user clicks a relative Markdown link (`./foo.md` etc.)
   * — receives the rewritten manifest-relative path. The renderer never
   * navigates the browser window for internal links.
   */
  onLinkNavigate(relPath: string): void;
}

type FetchState =
  | { kind: "loading" }
  | { kind: "success"; content: string }
  | { kind: "error" };

/**
 * Fetches and safely renders a Markdown doc from
 * `${PUBLIC_URL || ""}/docs/${docPath}`.
 *
 * @see DocRendererProps
 * @see renderMarkdownSafely
 */
export function DocRenderer(props: DocRendererProps): JSX.Element {
  const { docPath, anchor, onLinkNavigate } = props;
  const paneRef = React.useRef<HTMLDivElement>(null);

  // Seed the state from the cache on first render so cache hits skip the
  // brief loading flash entirely.
  const [state, setState] = React.useState<FetchState>(() => {
    const cached = docCache.get(docPath);
    return cached !== undefined
      ? { kind: "success", content: cached }
      : { kind: "loading" };
  });

  React.useEffect(() => {
    const cached = docCache.get(docPath);
    if (cached !== undefined) {
      // Cache hit — no network. Ensure state reflects this even on
      // docPath change (state initializer only runs on mount).
      setState({ kind: "success", content: cached });
      return;
    }

    setState({ kind: "loading" });
    const controller = new AbortController();
    const base = process.env.PUBLIC_URL ?? "";
    const url = `${base}/docs/${docPath}`;

    // Wrapped in an IIFE so the useEffect callback stays sync.
    void (async () => {
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (controller.signal.aborted) {
          return;
        }
        if (!res.ok) {
          setState({ kind: "error" });
          return;
        }
        const text = await res.text();
        if (controller.signal.aborted) {
          return;
        }
        cachePut(docPath, text);
        setState({ kind: "success", content: text });
      } catch (err) {
        // AbortError is the expected path on unmount / docPath change — do
        // NOT flip to error, and do NOT update state on an unmounted tree.
        if (
          err instanceof DOMException
            ? err.name === "AbortError"
            : (err as { name?: string })?.name === "AbortError"
        ) {
          return;
        }
        setState({ kind: "error" });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [docPath]);

  // Anchor scrolling — scoped to the pane, NEVER to window/document.body.
  React.useLayoutEffect(() => {
    if (state.kind !== "success" || anchor === undefined || anchor === "") {
      return;
    }
    const pane = paneRef.current;
    if (pane === null) {
      return;
    }
    // Anchors are constrained to /^[a-z0-9-]+$/ upstream, so a plain
    // template-literal selector is safe.
    //
    // `rehype-sanitize`'s defaultSchema prefixes heading IDs with
    // `user-content-` (DOM-clobbering guard). The manifest and deep-link
    // anchors are the raw slugs, so we try both — the raw slug first (in
    // case a future schema change removes the prefix) and the prefixed form
    // as the fallback.
    const target =
      pane.querySelector(`#${anchor}`) ??
      pane.querySelector(`#user-content-${anchor}`);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ block: "start", inline: "nearest" });
    }
  }, [state, anchor]);

  return (
    <div ref={paneRef} className="overflow-auto h-full">
      {state.kind === "loading" && (
        <div role="status" aria-busy="true">
          Loading…
        </div>
      )}
      {state.kind === "error" && (
        <div role="alert">
          This document is missing — the manifest may be stale. Please rebuild.
        </div>
      )}
      {state.kind === "success" &&
        renderMarkdownSafely(state.content, {
          currentDocPath: docPath,
          onLinkNavigate,
        })}
    </div>
  );
}
